import { useIsTrialing } from '@/modules/account'
import {
  CaseActivityCard,
  CaseMasthead,
  OverviewAccountMix,
  OverviewCaseFactsCard,
  OverviewCoverageCard,
  OverviewPortfolio,
  OverviewSkeleton,
  OverviewTasksCard,
  OverviewTopCategories,
  useLoadingProject,
  useLoadingTransactions,
  useTransactions,
} from '@/modules/projects'
import { cn } from '@/utils'
import { usePortfolioDashboard } from '../components/portfolio/usePortfolioDashboard'

interface PageProps {
  slug: string
}

// Overview is the case dashboard: a masthead, a row of detail cards, and the account-activity timeline.
//
// Coverage and account mix read across cash and brokerage, so they show on every case. Top categories
// and the activity timeline are cash-flow only, so a portfolio-only case drops them (they'd just show a
// misleading "no data"). A case still being set up keeps them so their empty states can guide the upload.
export function ProjectOverviewPage({ slug }: PageProps) {
  const transactions = useTransactions()
  const { accounts: portfolioAccounts } = usePortfolioDashboard()
  const isLoadingProject = useLoadingProject()
  const isLoadingTransactions = useLoadingTransactions()
  const isTrialing = useIsTrialing()

  if (isLoadingProject || isLoadingTransactions) {
    return <OverviewSkeleton />
  }

  const showCashFlowSections =
    transactions.length > 0 || portfolioAccounts.length === 0

  return (
    <div className="flex-1 min-h-0 flex flex-col gap-5">
      <CaseMasthead slug={slug} transactions={transactions} />
      <OverviewTasksCard slug={slug} />
      {!isTrialing && <OverviewCaseFactsCard slug={slug} />}
      <div className="@container">
        <div
          data-tour="case-summary"
          className={cn(
            'grid grid-cols-1 gap-4',
            showCashFlowSections ? '@4xl:grid-cols-3' : '@4xl:grid-cols-2',
          )}
        >
          <OverviewCoverageCard slug={slug} />
          <OverviewAccountMix />
          {showCashFlowSections ? (
            <OverviewTopCategories slug={slug} transactions={transactions} />
          ) : null}
        </div>
      </div>
      <OverviewPortfolio slug={slug} />
      {showCashFlowSections ? (
        <CaseActivityCard transactions={transactions} />
      ) : null}
    </div>
  )
}
